/**
 * workflowGraph — pure transform from a parsed ADR-075 dsl activity's `do`
 * task list into a flat node/edge graph description, ready to hand to a
 * layout pass (dagre) and then to @xyflow/react.
 *
 * Deliberately has ZERO knowledge of React Flow, dagre, or pixel positions —
 * it only decides WHICH nodes and edges exist and how they're labeled. This
 * keeps the trickiest part of the Workflows viewer (Phase 5a, PRD-workflow-editor
 * §4/§5) unit-testable without a DOM.
 *
 * Node language (PRD §4):
 *   agent   ▭ blue    — one node, data edges from `run`
 *   command ▭ green   — one node, data edges from each `args[i]`
 *   switch  ◇ branch  — one node; one labeled control edge per `case` + a
 *                       `default` edge, to either a named sibling task or an
 *                       inline branch (a nested do-list)
 *   fork    ⇉ parallel — one node; one control edge per branch (branches run
 *                       side-by-side, NOT nested — `parentId` passes through)
 *   for     ⟳ container — one node; body tasks get `parentId` = the for's own
 *                       id (so the UI can render them as "inside" the loop);
 *                       one control edge from the for into the body's first task
 *   run     ▭▭ purple  — one node labeled "run → <workflow>"; data edges from
 *                       each `inputs` value
 *   set     ▭ grey     — one node; data edges from each binding's expression
 *
 * Sequential `do` order = solid control edges between consecutive siblings
 * (top→bottom), drawn the way the runtime executes them (dsl_run.go
 * runTaskList / runSwitchTarget / runNamedTask):
 *   - A switch branch does NOT end the workflow. When the branch finishes, the
 *     switch's next sibling runs, so the edge into that sibling comes from the
 *     node(s) each branch finishes on — its "exits" (exitsOfEntry) — never
 *     from the switch node itself.
 *   - A task named as a switch target is routed-only: the runtime skips it in
 *     sequence and runs it only when a switch routes there. It gets no
 *     positional edge in or out; its continuation is the calling switch's.
 *   - A switch that is last in a branch hands each of its own branches on to
 *     the outer continuation. The last task of a `for` body or of the whole
 *     workflow has no continuation.
 *
 * `{{steps.X.output}}` / `.steps.X.output` data references are collected from
 * the SAME fields the canonical validator (scripts/validate-activities.py)
 * checks — agent.run, command.args[], switch case exprs, for.in, run.inputs
 * values, set values — and returned as `kind: 'data'` edges. The caller (the
 * Workflows page) decides whether to render them, behind the "show data
 * edges" toggle (off by default per PRD §4).
 */

// ADR-128 step 1 — the validator has accepted EIGHT task types since ADR-080
// added `python`; this union knew seven, and visitNode's `default` rendered
// nothing. The result was silent: 5 of Orion's 17 workflows dropped a `python`
// step from the graph with no warning anywhere, so the picture an operator
// reviewed was not the workflow that ran. `unknown` is the ninth member and is
// NOT a task type — it is how a future addition becomes visible instead of
// invisible (see visitNode's default).
export type TaskType =
  | 'agent' | 'command' | 'switch' | 'fork' | 'for' | 'run' | 'set' | 'python' | 'approval'
  | 'unknown'

/** A single node in the workflow graph — one per authored task. */
export interface GraphNode {
  /** The task's own name (unique across the whole activity). */
  id: string
  taskType: TaskType
  /** Display label — task name for most types; "run → <workflow>" and the
   * "for each X in Y (max N)" header are computed here so the UI doesn't
   * need to re-derive them. */
  label: string
  /** The task's own raw spec (the value under its type key), for the
   * Inspector panel. */
  spec: unknown
  /** Set only for tasks nested inside a `for` body — the id of the
   * enclosing `for` node. Undefined for everything else (switch/fork
   * branches are siblings, not nested, per PRD §4). */
  parentId?: string
}

/** A single edge in the workflow graph. */
export interface GraphEdge {
  id: string
  source: string
  target: string
  /** 'control' = the authored do/branch/loop wiring (solid, always shown).
   * 'data' = a {{steps.X.output}} reference (dotted, toggle-gated). */
  kind: 'control' | 'data'
  label?: string
}

export interface WorkflowGraph {
  nodes: GraphNode[]
  edges: GraphEdge[]
}

/** The ADR-075 Track 1 dsl marker (validate-activities.py's DSL_MARKER). */
export const DSL_MARKER = 'agentfleet/serverless-workflow/v1'

/** True when the parsed activity carries the dsl marker (vs. a legacy
 * stages/steps activity). */
export function isDialectActivity(activity: unknown): boolean {
  return isPlainObject(activity) && activity.dsl === DSL_MARKER
}

// ---------------------------------------------------------------------------
// {{steps.X.output}} / .steps.X.output reference scanning
// ---------------------------------------------------------------------------

const STEP_TEMPLATE_RE = /\{\{\s*steps\.([a-zA-Z0-9_-]+)\.output\s*\}\}/g
const STEP_EXPR_RE = /\.steps\.([a-zA-Z0-9_-]+)\.output/g

function stepRefsIn(text: unknown): string[] {
  if (typeof text !== 'string') return []
  const refs = new Set<string>()
  for (const m of text.matchAll(STEP_TEMPLATE_RE)) refs.add(m[1])
  for (const m of text.matchAll(STEP_EXPR_RE)) refs.add(m[1])
  return [...refs]
}

// ---------------------------------------------------------------------------
// Shape helpers
// ---------------------------------------------------------------------------

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

export interface ParsedEntry {
  name: string
  ttype: string
  spec: unknown
}

/** Parse one `{name: {ttype: spec}}` do-list entry. null if malformed
 * (already reported by the backend's server-side validation — this module
 * degrades gracefully rather than throwing). Exported so workflowDraft.ts
 * (the ADR-079 Phase 5b field-mode editor) shares the exact same do-entry
 * shape parsing as the read-side graph builder — one definition of "what a
 * task entry looks like". */
export function parseEntry(entry: unknown): ParsedEntry | null {
  if (!isPlainObject(entry)) return null
  const keys = Object.keys(entry)
  if (keys.length !== 1) return null
  const name = keys[0]
  const body = entry[name]
  if (!isPlainObject(body)) return null
  const bodyKeys = Object.keys(body)
  if (bodyKeys.length !== 1) return null
  const ttype = bodyKeys[0]
  return { name, ttype, spec: body[ttype] }
}

/** The first entry of a do-list that runs in sequence — the runtime skips
 * routed-only entries (runTaskList), so a container is entered at the first
 * task that is not one. */
function firstSequentialName(items: unknown, index: GraphIndex): string | null {
  if (!Array.isArray(items)) return null
  for (const item of items) {
    const parsed = parseEntry(item)
    if (parsed && !index.routed.has(parsed.name)) return parsed.name
  }
  return null
}

function makeEdge(source: string, target: string, kind: 'control' | 'data', label?: string): GraphEdge {
  const id = `${source}->${target}:${kind}${label ? `:${label}` : ''}`
  return label ? { id, source, target, kind, label } : { id, source, target, kind }
}

function addDataEdges(edges: GraphEdge[], refs: string[], targetName: string): void {
  for (const ref of refs) edges.push(makeEdge(ref, targetName, 'data'))
}

function fmt(v: unknown): string {
  if (v === undefined || v === null) return '?'
  return String(v)
}

// ---------------------------------------------------------------------------
// The tree walk
// ---------------------------------------------------------------------------

/** Every task entry in the activity by name, and the names any switch routes
 * to by string (routed-only — the runtime's dslIndexGraph). Task names are
 * unique across the activity (validator-enforced), so one flat index serves
 * every nesting level. */
interface GraphIndex {
  byName: Map<string, ParsedEntry>
  routed: Set<string>
}

function indexList(items: unknown, index: GraphIndex): void {
  if (!Array.isArray(items)) return
  for (const item of items) {
    const parsed = parseEntry(item)
    if (!parsed) continue
    index.byName.set(parsed.name, parsed)
    const specObj = isPlainObject(parsed.spec) ? parsed.spec : undefined
    if (parsed.ttype === 'for') indexList(specObj?.do, index)
    else if (parsed.ttype === 'fork') indexList(specObj?.branches, index)
    else if (parsed.ttype === 'switch' && Array.isArray(parsed.spec)) {
      for (const clause of parsed.spec) {
        if (!isPlainObject(clause)) continue
        const target = 'case' in clause ? clause.then : clause.default
        if (typeof target === 'string') index.routed.add(target)
        else indexList(target, index)
      }
    }
  }
}

/** The node(s) control is on when `entry` finishes and the next sibling
 * starts. Every task type is its own exit except a switch, whose exits are its
 * branches' exits. `visiting` stops mutually-routing switches from recursing
 * forever. */
function exitsOfEntry(entry: ParsedEntry, index: GraphIndex, visiting: Set<string>): string[] {
  if (entry.ttype !== 'switch' || !Array.isArray(entry.spec)) return [entry.name]
  const inner = new Set(visiting).add(entry.name)
  const exits: string[] = []
  for (const clause of entry.spec) {
    if (!isPlainObject(clause)) continue
    const target = 'case' in clause ? clause.then : clause.default
    let branchExits: string[] = []
    if (typeof target === 'string') {
      const named = index.byName.get(target)
      if (named && !inner.has(target)) branchExits = exitsOfEntry(named, index, inner)
    } else {
      branchExits = exitsOfList(target, index, inner)
    }
    // A branch with nothing to run continues from the switch itself.
    exits.push(...(branchExits.length > 0 ? branchExits : [entry.name]))
  }
  return [...new Set(exits)]
}

/** The exits of a do-list: those of its last entry that runs in sequence. */
function exitsOfList(items: unknown, index: GraphIndex, visiting: Set<string>): string[] {
  if (!Array.isArray(items)) return []
  for (let i = items.length - 1; i >= 0; i--) {
    const parsed = parseEntry(items[i])
    if (parsed && !index.routed.has(parsed.name)) return exitsOfEntry(parsed, index, visiting)
  }
  return []
}

function visitNode(
  name: string,
  ttype: string,
  spec: unknown,
  parentId: string | undefined,
  nodes: GraphNode[],
  edges: GraphEdge[],
  index: GraphIndex,
): void {
  const specObj = isPlainObject(spec) ? spec : undefined

  switch (ttype) {
    case 'agent': {
      nodes.push({ id: name, taskType: 'agent', label: name, spec, parentId })
      addDataEdges(edges, stepRefsIn(specObj?.run), name)
      return
    }
    case 'command': {
      nodes.push({ id: name, taskType: 'command', label: name, spec, parentId })
      const args = Array.isArray(specObj?.args) ? specObj.args : []
      for (const a of args) addDataEdges(edges, stepRefsIn(a), name)
      return
    }
    case 'approval': {
      // ADR-203 D1: the run parks here until an operator decides in Mission Control.
      nodes.push({ id: name, taskType: 'approval', label: name, spec, parentId })
      addDataEdges(edges, stepRefsIn(specObj?.title), name)
      addDataEdges(edges, stepRefsIn(specObj?.preview), name)
      return
    }
    case 'set': {
      nodes.push({ id: name, taskType: 'set', label: name, spec, parentId })
      if (specObj) {
        for (const v of Object.values(specObj)) addDataEdges(edges, stepRefsIn(v), name)
      }
      return
    }
    case 'run': {
      const workflow = typeof specObj?.workflow === 'string' ? specObj.workflow : ''
      nodes.push({ id: name, taskType: 'run', label: workflow ? `run → ${workflow}` : name, spec, parentId })
      const runInputs = isPlainObject(specObj?.inputs) ? specObj.inputs : undefined
      if (runInputs) {
        for (const v of Object.values(runInputs)) addDataEdges(edges, stepRefsIn(v), name)
      }
      return
    }
    case 'for': {
      const each = fmt(specObj?.each)
      const inExpr = fmt(specObj?.in)
      const max = fmt(specObj?.max)
      nodes.push({ id: name, taskType: 'for', label: `for each ${each} in ${inExpr} (max ${max})`, spec, parentId })
      addDataEdges(edges, stepRefsIn(specObj?.in), name)
      const body = specObj?.do
      visitList(body, name, nodes, edges, index)
      const firstBody = firstSequentialName(body, index)
      if (firstBody) edges.push(makeEdge(name, firstBody, 'control', 'loop body'))
      return
    }
    case 'fork': {
      nodes.push({ id: name, taskType: 'fork', label: name, spec, parentId })
      const branches = Array.isArray(specObj?.branches) ? specObj.branches : []
      for (const branch of branches) {
        const parsed = parseEntry(branch)
        if (!parsed) continue
        // Branches run side-by-side — siblings of the fork, not nested in it.
        visitNode(parsed.name, parsed.ttype, parsed.spec, parentId, nodes, edges, index)
        // runFork skips a routed-only branch; it runs only when a switch routes there.
        if (!index.routed.has(parsed.name)) edges.push(makeEdge(name, parsed.name, 'control', 'branch'))
      }
      return
    }
    case 'python': {
      // ADR-126 D1: for a `python` task, `inputs` is the ONLY way values enter
      // the code (validate-activities.py:652) — which makes it the highest-value
      // data-reference site in the most deterministic task type, and the one
      // whose edges matter most in the graph.
      nodes.push({ id: name, taskType: 'python', label: name, spec, parentId })
      const pyInputs = isPlainObject(specObj?.inputs) ? specObj.inputs : undefined
      if (pyInputs) {
        for (const v of Object.values(pyInputs)) addDataEdges(edges, stepRefsIn(v), name)
      }
      return
    }
    case 'switch': {
      nodes.push({ id: name, taskType: 'switch', label: name, spec, parentId })
      const clauses = Array.isArray(spec) ? spec : []
      for (const clause of clauses) {
        if (!isPlainObject(clause)) continue
        if ('case' in clause) {
          const caseExpr = clause.case
          addDataEdges(edges, stepRefsIn(caseExpr), name)
          const label = typeof caseExpr === 'string' ? caseExpr : 'case'
          wireSwitchTarget(name, clause.then, label, parentId, nodes, edges, index)
        } else if ('default' in clause) {
          wireSwitchTarget(name, clause.default, 'default', parentId, nodes, edges, index)
        }
      }
      return
    }
    default:
      // A task type this graph does not model yet. It is RENDERED, not skipped.
      //
      // The previous behaviour — "the backend's validation already flags this;
      // render nothing rather than guess at a shape" — was wrong in a way that
      // took two years to notice: the backend does NOT flag a valid task type
      // the frontend happens not to know, so `python` was silently absent from
      // 5 of Orion's 17 graphs. Rendering an explicit unknown node keeps the
      // graph honest about its own coverage: a ninth task type will look wrong
      // rather than look fine.
      nodes.push({ id: name, taskType: 'unknown', label: `${name} (${ttype})`, spec, parentId })
      return
  }
}

function wireSwitchTarget(
  switchName: string,
  target: unknown,
  label: string,
  parentId: string | undefined,
  nodes: GraphNode[],
  edges: GraphEdge[],
  index: GraphIndex,
): void {
  if (typeof target === 'string') {
    // A named-sibling "goto" — the target task is (or will be) visited as
    // its own do-list entry elsewhere; just wire the labeled branch edge.
    edges.push(makeEdge(switchName, target, 'control', label))
  } else if (Array.isArray(target)) {
    visitList(target, parentId, nodes, edges, index)
    const first = firstSequentialName(target, index)
    if (first) edges.push(makeEdge(switchName, first, 'control', label))
  }
}

/** Walk a do-list (top-level `do`, a for body, or a switch's inline branch)
 * in order. Each entry that runs in sequence gets a plain control edge from
 * the previous one's exits; routed-only entries are drawn but not chained
 * (see module doc). */
function visitList(items: unknown, parentId: string | undefined, nodes: GraphNode[], edges: GraphEdge[], index: GraphIndex): void {
  if (!Array.isArray(items)) return
  let prevExits: string[] = []
  for (const item of items) {
    const parsed = parseEntry(item)
    if (!parsed) continue
    visitNode(parsed.name, parsed.ttype, parsed.spec, parentId, nodes, edges, index)
    if (index.routed.has(parsed.name)) continue
    for (const exit of prevExits) edges.push(makeEdge(exit, parsed.name, 'control'))
    prevExits = exitsOfEntry(parsed, index, new Set())
  }
}

/**
 * Build the full node/edge graph for a parsed dsl activity's `do` list.
 * Returns an empty graph (no nodes/edges) if `activity.do` is missing or not
 * a list — callers should check {@link isDialectActivity} first and fall
 * back to the legacy stages/steps view otherwise.
 */
export function buildWorkflowGraph(activity: unknown): WorkflowGraph {
  const nodes: GraphNode[] = []
  const edges: GraphEdge[] = []
  const doList = isPlainObject(activity) ? activity.do : undefined
  const index: GraphIndex = { byName: new Map(), routed: new Set() }
  indexList(doList, index)
  visitList(doList, undefined, nodes, edges, index)
  // The same data reference in several fields of one task, or two identical
  // switch clauses, yields the same edge twice; React Flow needs each id once.
  const seen = new Set<string>()
  return { nodes, edges: edges.filter(e => !seen.has(e.id) && (seen.add(e.id), true)) }
}
